<template>
  <LMap
    ref="map"
    :zoom="mapZoom"
    :center="mapCenter"
    :options="mapOptions"
    class="mapa-claro"
    style="height: 400px; width: 100%;"
    @ready="onMapReady"
  >
    <!-- Mapa base: OpenStreetMap -->
    <LTileLayer :url="tileProvider.url" :attribution="tileProvider.attribution" />

    <!-- Marcadores de passageiros -->
    <template v-for="(passenger, index) in trip.passengers">
      <template v-for="(point, pIndex) in passenger.points">
        <LMarker
          v-if="hasValidCoords(point)"
          :key="`marker-${passenger.id}-${pIndex}`"
          :lat-lng="[parseFloat(point.latitude), parseFloat(point.longitude)]"
        >
          <LIcon :icon-anchor="[10, 10]" :icon-size="[20, 20]" class-name="custom-div-icon">
            <div
              :style="markerStyle(point.type)"
              class="custom-marker-icon"
              :title="point.type === 1 ? 'Embarque' : 'Desembarque'"
            />
          </LIcon>
        </LMarker>
      </template>
    </template>

    <!-- Pontos de parada da OS -->
    <template v-for="stop in (trip.stops || [])">
      <LMarker
        v-if="hasValidCoords(stop)"
        :key="`stop-${stop.id}`"
        :lat-lng="[parseFloat(stop.latitude), parseFloat(stop.longitude)]"
      >
        <LIcon :icon-anchor="[11, 11]" :icon-size="[22, 22]" class-name="custom-div-icon">
          <div
            class="custom-marker-icon stop-marker-icon"
            :class="{ 'stop-marker-icon--arrived': !!stop.arrived_at }"
            :title="`Parada ${stop.position}${stop.time ? ' (' + String(stop.time).slice(0, 5) + ')' : ''}: ${stop.address}${stop.arrived_at ? ' (chegada registrada)' : ''}`"
          >{{ stop.position }}</div>
        </LIcon>
      </LMarker>
    </template>

    <!-- Marcador do motorista (se status === 4) -->
    <LMarker
      v-if="showDriverMarker"
      :lat-lng="[parseFloat(trip.driver.last_position_lat), parseFloat(trip.driver.last_position_lng)]"
    >
      <LIcon :icon-anchor="[10, 10]" :icon-size="[20, 20]" class-name="custom-div-icon">
        <div
          style="background-color: #2196F3; border: 2px solid white;"
          class="custom-marker-icon"
          title="Motorista"
        />
      </LIcon>
    </LMarker>

    <!-- Rotas desenhadas -->
    <LPolyline
      v-for="(route, index) in routesData"
      :key="`route-${index}`"
      :lat-lngs="route.path"
      :color="route.color"
      :weight="6"
      :opacity="0.8"
    />

    <!-- Rota percorrida pelo motorista -->
    <LPolyline
      v-if="driverRoutePath.length > 1"
      :lat-lngs="driverRoutePath"
      color="#1565C0"
      :weight="5"
      :opacity="0.95"
    />

    <LMarker
      v-if="lastDriverRoutePoint"
      :lat-lng="[lastDriverRoutePoint.latitude, lastDriverRoutePoint.longitude]"
    >
      <LIcon :icon-anchor="[10, 10]" :icon-size="[20, 20]" class-name="custom-div-icon">
        <div
          style="background-color: #1565C0; border: 2px solid white;"
          class="custom-marker-icon"
          title="Última posição registrada"
        />
      </LIcon>
    </LMarker>
  </LMap>
</template>

<script>
import { LMap, LTileLayer, LMarker, LIcon, LPolyline } from 'vue2-leaflet';
import L from 'leaflet';
import axios from 'axios';
import _ from 'lodash';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
  name: 'TripMap',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPolyline,
  },
  props: {
    trip: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      mapCenter: { lat: -29.983164313555072, lng: -51.139299945774184 },
      mapZoom: 12,
      mapInstance: null,
      isLoadingRoutes: false,
      routesData: [],
      driverLocations: [],
      driverLocationPolling: null,
      lastDriverLocationId: null,
      osrmAxiosInstance: null,
      statusColors: {
        1: '#FFC400',
        2: '#039BE5',
        3: '#00C853',
        4: '#E53935',
      },
      // OpenStreetMap direto: o CARTO passou a exigir chave de API e
      // estampava "API KEY REQUIRED" por cima do mapa.
      tileProvider: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      },
      mapOptions: {
        zoomControl: true,
      },
    };
  },

  computed: {
    showDriverMarker() {
      const driver = this.trip.driver;
      return (
        driver &&
        Number(this.trip.status) === 4 &&
        driver.last_position_lat &&
        driver.last_position_lng
      );
    },

    driverRoutePath() {
      return this.driverLocations.map((point) => [point.latitude, point.longitude]);
    },

    lastDriverRoutePoint() {
      if (!this.driverLocations.length) return null;
      return this.driverLocations[this.driverLocations.length - 1];
    },
  },

  watch: {
    trip: {
      handler: _.debounce(function (newVal) {
        if (this.mapInstance && newVal.passengers.length) {
          this.drawRoutes(newVal.passengers);
        } else {
          this.clearRoutes();
        }

        this.resetDriverLocations();
        this.loadDriverLocations();
        this.startDriverLocationPolling();
      }, 500),
      immediate: true,
      deep: true,
    },
  },

  created() {
    this.osrmAxiosInstance = axios.create({ timeout: 10000 });
    delete this.osrmAxiosInstance.defaults.headers.common['Authorization'];
  },

  beforeDestroy() {
    this.stopDriverLocationPolling();
    if (this.mapInstance) this.mapInstance.remove();
  },

  methods: {
    onMapReady(map) {
      this.mapInstance = map;

      this.$nextTick(() => {
        setTimeout(() => {
          this.mapInstance.invalidateSize();
        }, 300);
      });

      if (this.trip.passengers.length) {
        this.drawRoutes(this.trip.passengers);
      }

      this.loadDriverLocations();
      this.startDriverLocationPolling();
    },

    hasValidCoords(point) {
      return point && point.latitude && point.longitude;
    },

    getMapStatusColor() {
      return this.statusColors[this.trip.status] || '#9E9E9E';
    },

    markerStyle(type) {
      return {
        backgroundColor: type === 1 ? '#4CAF50' : '#FF5722',
        border: '2px solid white',
      };
    },

    clearRoutes() {
      this.routesData = [];
    },

    resetDriverLocations() {
      this.driverLocations = [];
      this.lastDriverLocationId = null;
      this.stopDriverLocationPolling();
    },

    isTripInProgress() {
      return Number(this.trip.status) === 4;
    },

    async loadDriverLocations() {
      if (!this.trip || !this.trip.id) return;

      const params = {};
      if (this.lastDriverLocationId) params.after_id = this.lastDriverLocationId;

      try {
        const response = await axios.get(`/api/v2/trips/${this.trip.id}/locations`, { params });
        const payload = response.data && response.data.content ? response.data.content : {};
        const locations = Array.isArray(payload.data) ? payload.data : [];

        if (!locations.length) return;

        this.mergeDriverLocations(locations);
        this.lastDriverLocationId = payload.last_id || this.lastDriverLocationId;
        this.fitBoundsToRoutes();
      } catch (error) {
        console.error(`Erro ao carregar rota percorrida da OS ${this.trip.id}`, error);
      }
    },

    mergeDriverLocations(locations) {
      const byId = new Map(this.driverLocations.map((point) => [point.id, point]));

      for (const location of locations) {
        const point = {
          ...location,
          latitude: parseFloat(location.latitude),
          longitude: parseFloat(location.longitude),
        };

        if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) continue;

        byId.set(point.id, point);
      }

      this.driverLocations = Array.from(byId.values()).sort((a, b) => a.id - b.id);
    },

    startDriverLocationPolling() {
      this.stopDriverLocationPolling();
      if (!this.isTripInProgress()) return;

      this.driverLocationPolling = setInterval(() => {
        this.loadDriverLocations();
      }, 10000);
    },

    stopDriverLocationPolling() {
      if (!this.driverLocationPolling) return;

      clearInterval(this.driverLocationPolling);
      this.driverLocationPolling = null;
    },

    fitBoundsToRoutes() {
      if (!this.mapInstance) return;

      const bounds = new L.LatLngBounds();

      for (const route of this.routesData) {
        for (const point of route.path) {
          bounds.extend(point);
        }
      }

      for (const point of this.driverRoutePath) {
        bounds.extend(point);
      }

      for (const stop of (this.trip.stops || [])) {
        if (this.hasValidCoords(stop)) bounds.extend([parseFloat(stop.latitude), parseFloat(stop.longitude)]);
      }

      if (this.showDriverMarker) {
        bounds.extend([
          parseFloat(this.trip.driver.last_position_lat),
          parseFloat(this.trip.driver.last_position_lng),
        ]);
      }

      if (bounds.isValid()) {
        this.mapInstance.invalidateSize();
        this.mapInstance.fitBounds(bounds, { padding: [50, 50] });
      }
    },

    async drawRoutes(passengers) {
      this.clearRoutes();
      this.isLoadingRoutes = true;

      const bounds = new L.LatLngBounds();
      const calculatedRouteDetails = [];

      for (const passenger of passengers) {
        const pickup = passenger.points.find(p => p.type === 1);
        const dropoff = passenger.points.find(p => p.type === 2);

        if (!this.hasValidCoords(pickup) || !this.hasValidCoords(dropoff)) {
          calculatedRouteDetails.push({
            passengerId: passenger.id,
            distance: null,
            duration: null,
          });
          continue;
        }

        const origin = `${pickup.longitude},${pickup.latitude}`;
        const destination = `${dropoff.longitude},${dropoff.latitude}`;
        const url = `https://router.project-osrm.org/route/v1/driving/${origin};${destination}?overview=full&geometries=geojson`;

        try {
          const response = await this.osrmAxiosInstance.get(url);
          const route = response.data.routes[0];

          if (route) {
            const latLngPath = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);

            this.routesData.push({
              path: latLngPath,
              color: this.getMapStatusColor(),
            });

            bounds.extend([parseFloat(pickup.latitude), parseFloat(pickup.longitude)]);
            bounds.extend([parseFloat(dropoff.latitude), parseFloat(dropoff.longitude)]);

            calculatedRouteDetails.push({
              passengerId: passenger.id,
              distance: parseFloat((route.distance / 1000).toFixed(2)),
              duration: parseInt(route.duration / 60),
            });
          } else {
            calculatedRouteDetails.push({
              passengerId: passenger.id,
              distance: null,
              duration: null,
            });
          }
        } catch (error) {
          console.error(`Erro ao obter rota para passageiro ${passenger.id}`, error);
          calculatedRouteDetails.push({
            passengerId: passenger.id,
            distance: null,
            duration: null,
          });
        }
      }

      // Inclui a posição do motorista no bounds (se necessário)
      if (this.showDriverMarker) {
        bounds.extend([
          parseFloat(this.trip.driver.last_position_lat),
          parseFloat(this.trip.driver.last_position_lng),
        ]);
      }

      this.isLoadingRoutes = false;
      this.$emit('routes-calculated', calculatedRouteDetails);

      this.$nextTick(() => {
        this.fitBoundsToRoutes();
      });
    },
  },
};
</script>

<style scoped>
/* Visual claro estilo "Positron": dessatura so a camada de tiles do OSM.
   Rotas, pins e controles ficam em panes separados e mantem as cores. */
.mapa-claro ::v-deep .leaflet-tile-pane {
  filter: grayscale(100%) brightness(104%) contrast(88%);
}
.custom-marker-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stop-marker-icon {
  width: 22px;
  height: 22px;
  background-color: #F57C00;
  border: 2px solid white;
  color: white;
  font-size: 11px;
  font-weight: 700;
}

.stop-marker-icon--arrived {
  background-color: #2E7D32;
}
</style>
